import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Check, 
  Calendar, 
  Users, 
  MapPin, 
  Clock, 
  ArrowLeft, 
  ArrowRight,
  Mail, 
  Phone,
  ChevronRight,
  Info
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const PackageDetailPage = () => {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        const data = await response.json();
        setPkg(data);
      } catch (error) {
        console.error('Error fetching package:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Pakken blev ikke fundet</h1>
        <Link to="/" className="text-emerald-600 font-semibold flex items-center gap-2">
          <ArrowLeft size={20} /> Tilbage til forsiden
        </Link>
      </div>
    );
  }

  // Specific content for "Sociale golfophold"
  const isSocial = slug === 'sociale-golfophold';

  const socialContent = {
    highlights: [
      "7 overnatninger i delt dobbeltværelse",
      "5 runder golf på udvalgte baner",
      "Fælles velkomstmiddag og afslutningsmiddag",
      "Dansk matchleder under hele opholdet",
      "Præmier til dagens vindere",
      "Socialt samvær med andre golfentusiaster"
    ],
    program: [
      { day: "Dag 1", title: "Ankomst & Velkomst", desc: "Ankomst til hotellet, indtjekning og fælles velkomstmiddag om aftenen." },
      { day: "Dag 2", title: "Første runde golf", desc: "Vi spiller den første runde på en af de lokale baner. Hygge i klubhuset efterfølgende." },
      { day: "Dag 3", title: "Turnering & Frokost", desc: "Dagens match med indlagte konkurrencer. Fælles frokost efter runden." },
      { day: "Dag 4", title: "Hviledag / Udflugt", desc: "Mulighed for at udforske Cádiz eller slappe af ved stranden." },
      { day: "Dag 5", title: "Golf i solskinnet", desc: "Endnu en fantastisk dag på banen med fokus på det sociale spil." },
      { day: "Dag 6", title: "Finaledag", desc: "Den store finale-match efterfulgt af præmieoverrækkelse." },
      { day: "Dag 7", title: "Afslutning & Hjemrejse", desc: "Morgenmad og afrejse mod Danmark efter en uge fyldt med gode minder." }
    ]
  };

  const defaultHighlights = [
    "Skræddersyet rejseplan",
    "Personlig rådgivning",
    "Håndplukkede hoteller",
    "Garanterede starttider",
    "Lokal support under opholdet",
    "Fleksibel booking"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pkg.title.da} | Golf4Life</title>
        <meta name="description" content={pkg.description.da} />
      </Helmet>

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[40vh] min-h-[300px] flex items-center">
          <div className="absolute inset-0">
            <img 
              src={pkg.image[0]} 
              alt={pkg.title.da} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-slate-900/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-4xl md:text-6xl font-bold text-white uppercase max-w-3xl leading-tight mb-8">
              {pkg.title.da}
            </h1>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/kontakt" 
                className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 rounded-full border-2 border-white bg-transparent text-white font-semibold text-base md:text-lg hover:-translate-y-1 transition-all shadow-lg shadow-black/20 group"
              >
                Book dit ophold
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              
              {/* Left Column: Details */}
              <div className="lg:col-span-2">
                <div className="prose prose-lg max-w-none text-slate-600 mb-12">
                  <h2 className="text-3xl font-semibold text-slate-900 mb-6 uppercase">Om opholdet</h2>
                  <p className="text-xl leading-relaxed">
                    {pkg.description.da}
                  </p>
                  <p className="mt-4">
                    Hos Golf4Life går vi op i, at dit golfophold bliver præcis som du drømmer om. Vi har nøje udvalgt de bedste baner og hoteller i området omkring Cádiz, så du er sikret en oplevelse i topklasse.
                  </p>
                </div>

                {isSocial && (
                  <div className="mb-16">
                    <h2 className="text-2xl font-semibold text-slate-900 mb-8 uppercase">Program for ugen</h2>
                    <div className="space-y-6">
                      {socialContent.program.map((item, idx) => (
                        <div key={idx} className="flex gap-6 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                          <div className="shrink-0 w-16 h-16 bg-emerald-600 text-white rounded-xl flex flex-col items-center justify-center font-bold">
                            <span className="text-xs uppercase opacity-80">Dag</span>
                            <span className="text-xl">{idx + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1 uppercase">{item.title}</h3>
                            <p className="text-slate-600">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
                  <h2 className="text-2xl font-semibold mb-8 uppercase text-slate-900">Hvad er inkluderet?</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(isSocial ? socialContent.highlights : defaultHighlights).map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check size={24} className="text-slate-900 shrink-0" />
                        <span className="text-slate-600 pt-0.5">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Sidebar / Booking */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-8">
                  <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 uppercase">Interesseret?</h3>
                    <p className="text-slate-600 mb-8">
                      Kontakt os for at høre mere om ledige datoer og få et uforpligtende tilbud tilpasset dine ønsker.
                    </p>
                    
                    <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-4 text-slate-700">
                        <Calendar size={20} className="text-slate-900 shrink-0" />
                        <span>Fleksible datoer</span>
                      </div>
                      <div className="flex items-center gap-4 text-slate-700">
                        <Users size={20} className="text-slate-900 shrink-0" />
                        <span>For både singler & par</span>
                      </div>
                      <div className="flex items-center gap-4 text-slate-700">
                        <MapPin size={20} className="text-slate-900 shrink-0" />
                        <span>Cádiz, Spanien</span>
                      </div>
                    </div>

                    <Link 
                      to="/kontakt" 
                      className="block w-full py-4 px-6 bg-emerald-600 text-white text-center font-bold rounded-xl hover:-translate-y-1 transition-all uppercase tracking-wide"
                    >
                      Forespørg nu
                    </Link>
                  </div>

                  <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                    <h4 className="font-bold text-slate-900 mb-4 uppercase">Har du spørgsmål?</h4>
                    <div className="space-y-4">
                      <a href="tel:+4522467722" className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors">
                        <Phone size={18} />
                        <span>+45 22 46 77 22</span>
                      </a>
                      <a href="mailto:info@golf4life.dk" className="flex items-center gap-3 text-slate-600 hover:text-emerald-600 transition-colors">
                        <Mail size={18} />
                        <span>info@golf4life.dk</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PackageDetailPage;
